import { useMutation, useQueryClient } from "@tanstack/react-query";
import { pick } from "es-toolkit";
import { useCallback, useEffect, useRef } from "react";
import { Controller, type UseFormSetError, useForm } from "react-hook-form";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";
import { useBreadCrumbContext } from "@/contexts/breadcrumb";
import { validationErrors } from "@/helpers";
import { signOut as apiSignout, updateUserParams } from "../../api";
import {
	Button,
	FormContainer,
	FormSection,
	InputText,
} from "../../components";
import { useCurrentToken, useCurrentUser } from "../../hooks";

interface UserFormProps {
	firstname: string;
	lastname: string;
	locale: string;
}

interface PasswordFormProps {
	currentPassword: string;
	password: string;
	passwordConfirmation: string;
}

const Account = () => {
	const { t } = useTranslation();
	useBreadCrumbContext([{ label: t("account.pageTitle") }]);
	const queryClient = useQueryClient();
	const { data: currentUserData } = useCurrentUser();
	const { data: currentTokenData } = useCurrentToken();
	const toastId = useRef<string>(null);
	const {
		control: userFormControl,
		handleSubmit: handleUserFormSubmit,
		reset,
		setError: setUserFormError,
	} = useForm<UserFormProps>({
		defaultValues: { firstname: "", lastname: "" },
	});

	const {
		control: passwordFormControl,
		handleSubmit: handlePasswordFormSubmit,
		reset: resetPasswordForm,
		setError: setPasswordFormError,
	} = useForm<PasswordFormProps>({
		defaultValues: {
			currentPassword: "",
			password: "",
			passwordConfirmation: "",
		},
	});

	// `PATCH /v2/current_user` answers 422 with `{ attribute: [message] }` when the
	// update is refused (wrong current password, weak password, ...). Only a 2xx
	// means the change went through, so surface the errors on the matching fields
	// instead of assuming success.
	const applyServerErrors = useCallback(
		<T extends UserFormProps | PasswordFormProps>(
			error: unknown,
			setError: UseFormSetError<T>,
			knownFields: (keyof T)[],
		) => {
			const errors = validationErrors(error);

			const unmapped = Object.entries(errors)
				.filter(([field, message]) => {
					if (!knownFields.includes(field as keyof T)) {
						return true;
					}
					setError(field as never, { type: "server", message });
					return false;
				})
				.map(([field, message]) => `${field} ${message}`);

			// A 422 body we could not attribute to a field (or a network/500 error)
			// still has to be shown, otherwise the form looks like it saved.
			toast.error(
				Object.keys(errors).length === 0
					? t("global.savingError")
					: [t("account.updateRefused"), ...unmapped].join(" — "),
				{ id: toastId?.current || undefined },
			);
		},
		[t],
	);

	const { mutate: updateApiUser, isPending: userSubmitIsLoading } = useMutation(
		{
			mutationFn: (data: UserFormProps) => updateUserParams(data),
			onMutate: () => {
				toastId.current = toast.loading(t("global.loading"));
			},
			onSuccess: () => {
				toast.success(t("global.saved"), { id: toastId?.current || undefined });
				queryClient.invalidateQueries({ queryKey: ["currentUser"] });
			},
			onError: (error) => {
				applyServerErrors<UserFormProps>(error, setUserFormError, [
					"firstname",
					"lastname",
					"locale",
				]);
			},
		},
	);

	// Changing the password requires re-authenticating with the current one, so a
	// stolen access token alone cannot take the account over.
	const { mutate: updateApiPassword, isPending: passwordSubmitIsLoading } =
		useMutation({
			mutationFn: (data: PasswordFormProps) => updateUserParams(data),
			onMutate: () => {
				toastId.current = toast.loading(t("global.loading"));
			},
			onSuccess: () => {
				toast.success(t("global.saved"), { id: toastId?.current || undefined });
				resetPasswordForm({
					currentPassword: "",
					password: "",
					passwordConfirmation: "",
				});
				queryClient.invalidateQueries({ queryKey: ["currentUser"] });
			},
			onError: (error) => {
				applyServerErrors<PasswordFormProps>(error, setPasswordFormError, [
					"currentPassword",
					"password",
					"passwordConfirmation",
				]);
			},
		});

	const { mutate: signOut } = useMutation({
		mutationFn: () => apiSignout({ token: currentTokenData }),
		onMutate: () => {
			toastId.current = toast.loading(t("global.loading"));
		},
		onSuccess: () => {
			toast.success(t("global.saved"), { id: toastId?.current || undefined });
			localStorage.clear();
			queryClient.getQueryCache().clear();
		},
		onError: () => {
			toast.error(t("global.savingError"), {
				id: toastId?.current || undefined,
			});
		},
	});

	// Profile-only changes (firstname/lastname/locale) do not touch credentials,
	// so the API does not ask for the current password here.
	const onUserSubmit = useCallback(
		(data: UserFormProps) => {
			updateApiUser(data);
		},
		[updateApiUser],
	);

	const onPasswordSubmit = useCallback(
		(data: PasswordFormProps) => {
			updateApiPassword(data);
		},
		[updateApiPassword],
	);

	useEffect(() => {
		reset(
			currentUserData?.data
				? pick(currentUserData.data, ["firstname", "lastname", "locale"])
				: {},
		);
	}, [currentUserData, reset]);

	const renderUpdateUserForm = useCallback(() => {
		return (
			<form
				className="flex flex-col gap-8"
				onSubmit={handleUserFormSubmit(onUserSubmit)}
			>
				<Controller
					control={userFormControl}
					name="firstname"
					render={({ field: { onChange, value }, fieldState: { error } }) => (
						<InputText
							error={error?.message}
							onChange={onChange}
							value={value}
							placeholder={t("account.firstnamePlaceholder")}
							type="text"
							label={t("account.firstnameLabel")}
						/>
					)}
				/>
				<Controller
					control={userFormControl}
					name="lastname"
					render={({ field: { onChange, value }, fieldState: { error } }) => (
						<InputText
							error={error?.message}
							onChange={onChange}
							value={value}
							placeholder={t("account.lastnamePlaceholder")}
							type="text"
							label={t("account.lastnameLabel")}
						/>
					)}
				/>

				<Button
					className="self-end"
					type="submit"
					disabled={userSubmitIsLoading}
				>
					{t("global.save")}
				</Button>
			</form>
		);
	}, [
		t,
		handleUserFormSubmit,
		onUserSubmit,
		userFormControl,
		userSubmitIsLoading,
	]);

	const renderUpdatePasswordForm = useCallback(() => {
		return (
			<form
				className="flex flex-col gap-8"
				onSubmit={handlePasswordFormSubmit(onPasswordSubmit)}
			>
				<Controller
					control={passwordFormControl}
					name="currentPassword"
					rules={{ required: t("account.currentPasswordRequired") }}
					render={({ field: { onChange, value }, fieldState: { error } }) => (
						<InputText
							error={error?.message}
							onChange={onChange}
							value={value}
							placeholder={t("account.currentPasswordPlaceholder")}
							type="password"
							autoComplete="current-password"
							label={t("account.currentPasswordLabel")}
							hint={t("account.currentPasswordHint")}
						/>
					)}
				/>

				<Controller
					control={passwordFormControl}
					name="password"
					rules={{
						required: t("account.passwordRequired"),
						validate: (value, { passwordConfirmation }) =>
							value === passwordConfirmation ||
							t("account.passwordConfirmationMismatch"),
					}}
					render={({ field: { onChange, value }, fieldState: { error } }) => (
						<InputText
							error={error?.message}
							onChange={onChange}
							value={value}
							placeholder={t("account.passwordPlaceholder")}
							type="password"
							autoComplete="new-password"
							label={t("account.passwordLabel")}
						/>
					)}
				/>

				<Controller
					control={passwordFormControl}
					name="passwordConfirmation"
					render={({ field: { onChange, value }, fieldState: { error } }) => (
						<InputText
							error={error?.message}
							onChange={onChange}
							value={value}
							placeholder={t("account.passwordConfirmationPlaceholder")}
							type="password"
							autoComplete="new-password"
							label={t("account.passwordConfirmationLabel")}
						/>
					)}
				/>
				<Button
					className="self-end"
					type="submit"
					disabled={passwordSubmitIsLoading}
				>
					{t("global.save")}
				</Button>
			</form>
		);
	}, [
		t,
		handlePasswordFormSubmit,
		onPasswordSubmit,
		passwordFormControl,
		passwordSubmitIsLoading,
	]);

	return (
		<FormContainer
			isNotForm
			title={t("account.pageTitle")}
			subtitle={t("account.pageSubtitle")}
			button={
				<Button type="button" variant="outline" onClick={() => signOut()}>
					{t("account.signOut")}
				</Button>
			}
		>
			<FormSection title={t("account.generalInformations")}>
				{renderUpdateUserForm()}
			</FormSection>
			<FormSection title={t("account.security")}>
				{renderUpdatePasswordForm()}
			</FormSection>
		</FormContainer>
	);
};

export default Account;
